<template>
  <PageVC class="booking-cart">
    <div class="a-container">
      <h1 class="a-page-title">Kurv</h1>
      <HUILoadingView v-if="!booking || !order || !menu || !entered " />
      <template v-else-if="sortedOrderLines">
        <div class="booking-cart__prefix a-rich-text a-rich-text--centered">
          <p>Forudbestilt mad til Halifax {{ restaurantName }}, {{ computedTime }}.</p>
        </div>
        <div class="a-container__offset">
          <Cart
            :order="order"
            :menu="menu"
            :sorted-order-lines="sortedOrderLines"
            :expand-others="sortedOrderLines.you.orderLines.length > 0 ? false : true"
            :mark-ready-order-lines="true"
            @burger-update="handleCartUpdateBurger"
            @burger-delete="handleCartDeleteBurger"
            @product-change="handleCartProductChange"
            :disabled="busy"
          ></Cart>
        </div>
        <HUIButton
          class="booking-cart__done"
          @click="handleReadyClick"
          v-if="usersOrderLines.length > 0 && usersOrderLinesMarkedAsReady.length === 0"
          theme="green"
          :spinner="busy"
          :disabled="busy"
        >Afslut min bestilling</HUIButton>
        <HUIButton
          class="booking-cart__done"
          @click="handleOrderMoreClick"
          v-if="usersOrderLines.length > 0 && usersOrderLinesMarkedAsReady.length === 0"
          :disabled="busy"
        >Bestil mere</HUIButton>
        <template v-else-if="usersOrderLines.length > 0 && usersOrderLinesMarkedAsReady.length > 0">
          <RouterButton
            theme="green"
            class="booking-cart__done"
            :to="{ name: routes.HOME }"
          >Tilbage til forsiden</RouterButton>
          <HUIButton
            class="booking-cart__done"
            @click="handleNotReadyClick"
            :spinner="busy"
            :disabled="busy"
          >Ret i min bestilling</HUIButton>
        </template>
      </template>
      <template v-else>
        <div
          class="booking-cart__empty-text a-rich-text a-rich-text--centered"
        >Der er endnu ikke forudbestilt noget mad.</div>
        <RouterButton
          theme="green"
          :to="{ name: routes.BOOKING_PREORDER_MENU, params: { bookingToken: $route.params.bookingToken }}"
        >Forudbestil mad</RouterButton>
      </template>
    </div>
  </PageVC>
</template>

<script>
import { mapState } from 'vuex'
import { HUILoadingView, HUIButton } from '@/halifax-ui'
import BookingPreorderBase from '@/views/BookingPreorderBase'
import PageVC from '@/containers/PageVC'
import Cart from '@/containers/Cart'
import RouterButton from '@/containers/RouterButton'
import sortsOrderLinesOnChange from '@/mixins/sortsOrderLinesOnChange'
import routes from '@/router/routes'
import { ORIGIN } from '@/api'
import { getDateString } from '@/utils/dateHelpers'
import filterAndMap from '@/utils/filterAndMap'

export default {
  extends: BookingPreorderBase,
  mixins: [sortsOrderLinesOnChange],
  components: {
    PageVC,
    Cart,
    HUILoadingView,
    RouterButton,
    HUIButton
  },
  data() {
    return {
      routes,
      busy: false
    }
  },
  computed: {
    ...mapState('restaurant', {
      restaurants: 'restaurants'
    }),
    payments() {
      return this.order && this.order.payments
    },
    restaurantName() {
      return this.restaurants.find(restaurant => restaurant.id === this.booking.restaurant_id).name
    },
    computedTime() {
      // 2018/08/10 14:30:00.00000 -> 2018-08-10 14:30:00 for Safari
      const date = new Date(this.booking.starts_at.replace(/-/g, '/').substring(0, 19))
      return getDateString(date, { date: '2-digit', month: '2-digit' })
    },
    usersOrderLines() {
      return this.sortedOrderLines ? this.sortedOrderLines.you.orderLines : []
    },
    usersOrderLinesMarkedAsReady() {
      return this.usersOrderLines.filter(orderLine => !!orderLine.is_ready)
    }
  },
  methods: {
    async handleCartDeleteBurger(config) {
      try {
        await this.$store.dispatch('order/REMOVE_ITEM', {
          orderLineToken: config.orderLineToken,
          orderToken: this.orderToken
        })
      } catch (error) {
        this.$store.dispatch('toast/ERROR', error.message)
      }
    },
    async handleCartUpdateBurger(config) {
      const payload = {
        type: 'burger',
        burger_id: config.id,
        bun_id: config.bun,
        patty_id: config.patty,
        frying_id: config.frying,
        ingredients: config.ingredients,
        quantity: config.quantity
      }
      try {
        await this.$store.dispatch('order/UPDATE_ITEM', {
          payload,
          orderLineToken: config.orderLineToken,
          orderToken: this.orderToken
        })
      } catch (error) {
        this.$store.dispatch('toast/ERROR', error.message)
      }
    },
    async handleCartProductChange({ productId, quantity, orderLineToken }) {
      if (quantity === 0) {
        try {
          await this.$store.dispatch('order/REMOVE_ITEM', {
            orderToken: this.orderToken,
            orderLineToken
          })
        } catch (error) {
          this.$store.dispatch('toast/ERROR', error.message)
        }
      } else {
        const payload = {
          type: 'product',
          product_id: productId,
          quantity
        }
        try {
          await this.$store.dispatch('order/UPDATE_ITEM', {
            payload,
            orderToken: this.orderToken,
            orderLineToken
          })
        } catch (error) {
          this.$store.dispatch('toast/ERROR', error.message)
        }
      }
    },
    handleOrderMoreClick() {
      this.$router.go(-1)
    },
    async handleReadyClick() {
      try {
        this.busy = true
        await this.$store.dispatch('order/MARK_READY', this.orderToken)
        this.$store.dispatch('dialog/ALERT', {
          props: {
            heading: 'Tak for din bestilling',
            body:
              'En tjener vil efter aftale med jer, sætte gang i jeres ordre, når i ankommer til restauranten.'
          }
        })
      } catch (error) {
      } finally {
        this.busy = false
      }
    },
    async handleNotReadyClick() {
      try {
        this.busy = true
        await this.$store.dispatch('order/MARK_NOT_READY', this.orderToken)
      } catch (error) {
      } finally {
        this.busy = false
      }
    }
  }
}
</script>

<style lang="scss">
@import '~@/scss/variables';
@import '~@/halifax-ui/src/scss/framework';

.booking-cart {
  $module: '.booking-cart';

  &__prefix {
    margin-bottom: 40px;
    margin-top: -30px;
  }
  &__suffix {
    margin-top: 24px;
    // color: $halifax-light-brown;
    font-style: italic;
    font-size: 1.4rem;

    a {
      text-decoration: underline;
      color: inherit;
    }
  }
  &__empty-text {
    margin-bottom: 50px;
  }
  &__done {
    margin-top: 40px;

    + #{$module}__done {
      margin-top: 10px;
    }
  }
}
</style>
